import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/hooks/errors/use-toast";
import { FREEMIUM_PLAN_ID } from '@/config/constants';

export function useSignup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignup = async () => {
    try {
      setIsSubmitting(true);
      
      // Attempt to sign up the user (BotID protection handled by API)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role: 'user',
          }
        }
      });

      // Check for specific error conditions
      if (error) {
        console.error("Signup error details:", error);
        
        // Handle specific error codes
        if (error.message?.toLowerCase().includes("user already registered") || 
            error.message?.toLowerCase().includes("already in use") ||
            error.message?.toLowerCase().includes("already exists")) {
          // User already exists - show toast and redirect to sign in page
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Redirecting to sign in...",
            variant: "warning",
          });
          
          // Small delay to ensure toast is visible before redirect
          setTimeout(() => {
            navigate('/signin', { 
              state: { 
                email,
                showAccountExistsMessage: false
              } 
            });
          }, 1500);
          return false;
        }
        
        // Handle server errors (500)
        if (error.status === 500) {
          toast({
            title: "Registration Error",
            description: "There was a problem with the registration service. Please try again later or contact support if the issue persists.",
            variant: "error",
          });
          return false;
        }
        
        throw error;
      }

      // If identities array is empty, it means the user already exists but hasn't confirmed their email
      if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
        console.log("User already exists (empty identities), redirecting to sign in");
        
        // Show toast notification for existing user
        toast({
          title: "Account Already Exists",
          description: "This email is already registered. Redirecting to sign in...",
          variant: "warning",
        });
        
        // Small delay to ensure toast is visible before redirect
        setTimeout(() => {
          navigate('/signin', { 
            state: { 
              email,
              showAccountExistsMessage: false
            } 
          });
        }, 1500);
        return false;
      }

      // SUCCESS: New user created in auth.users
      console.log("🎯 AUTH SIGNUP SUCCESS - User created in auth.users:", {
        userId: data?.user?.id,
        email: data?.user?.email,
        hasIdentities: data?.user?.identities?.length > 0
      });

      // Now manually create the user profile (triggers will handle the rest)
      if (data?.user?.id) {
        console.log("🔄 ATTEMPTING to create user profile in public.users table...");
        
        const userProfileData = {
          id: data.user.id,
          email: data.user.email || email,
          current_subscription: FREEMIUM_PLAN_ID, // Freemium plan
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        console.log("📝 User profile data to insert:", userProfileData);
        
        try {
          console.log("⏳ Executing supabase.from('users').insert()...");
          
          const { error: profileError, data: insertResult } = await supabase
            .from('users')
            .insert(userProfileData);

          console.log("📊 Insert result:", { error: profileError, data: insertResult });

          if (profileError) {
            console.error("❌ ERROR creating user profile:", {
              error: profileError,
              code: profileError.code,
              message: profileError.message,
              details: profileError.details,
              hint: profileError.hint
            });
            // Don't fail the signup, just log the error
            // The user can still sign in and we can handle profile creation later
          } else {
            console.log("✅ SUCCESS: User profile created successfully in public.users");
            console.log("🎉 Profile creation completed, triggers should now initialize user data");
          }
        } catch (profileError) {
          console.error("💥 EXCEPTION during user profile creation:", {
            error: profileError,
            stack: profileError instanceof Error ? profileError.stack : 'No stack trace'
          });
          // Don't fail the signup, just log the error
        }
      } else {
        console.error("⚠️ WARNING: No user ID found after successful auth signup!", {
          userData: data?.user,
          hasUser: !!data?.user,
          userId: data?.user?.id
        });
      }

      // Navigate to verify email page with success flag
      navigate('/verify-email', { 
        state: { 
          email,
          showSuccessToast: true // Flag to indicate we just registered successfully
        } 
      });
      return true;

    } catch (error: any) {
      console.error("Registration error:", error);
      
      // Improved error handling with more user-friendly messages
      let errorMessage = "An unexpected error occurred during registration";
      
      if (error.message) {
        if (error.message.toLowerCase().includes("duplicate key") || 
            error.message.toLowerCase().includes("already registered") || 
            error.message.toLowerCase().includes("already in use") ||
            error.message.toLowerCase().includes("already exists")) {
          // User already exists - show toast and redirect to sign in page
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Redirecting to sign in...",
            variant: "warning",
          });
          
          // Small delay to ensure toast is visible before redirect
          setTimeout(() => {
            navigate('/signin', { 
              state: { 
                email,
                showAccountExistsMessage: false
              } 
            });
          }, 1500);
          return false;
        } else if (error.message.toLowerCase().includes("password")) {
          errorMessage = "Password error: " + error.message;
        } else if (error.message.toLowerCase().includes("email")) {
          errorMessage = "Email error: " + error.message;
        } else {
          errorMessage = error.message;
        }
      }
      
      // Handle server errors
      if (error.status === 500) {
        errorMessage = "There was a problem with the registration service. Please try again later or contact support if the issue persists.";
      }
      
      toast({
        title: "Registration Error",
        description: errorMessage,
        variant: "error",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    isSubmitting,
    handleSignup
  };
}
